import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Loader2, ChevronLeft, ChevronRight, ArrowLeft, Shield, AlertTriangle,
  Lock, Camera, ImagePlus, Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useAuth } from '@/lib/auth';
import { usePhotoAudit, type PhotoAuditEventType, type PhotoAuditEvent } from '@/hooks/usePhotoAudit';

const EVENT_ICONS: Record<PhotoAuditEventType, React.ElementType> = {
  month_lock_overdue_override: Lock,
  late_photo_upload:           Camera,
  gallery_upload_override:     ImagePlus,
  confirm_without_photo:       Eye,
};

const EVENT_COLORS: Record<PhotoAuditEventType, string> = {
  month_lock_overdue_override: 'text-rose-600',
  late_photo_upload:           'text-amber-600',
  gallery_upload_override:     'text-indigo-600',
  confirm_without_photo:       'text-muted-foreground',
};

export default function PhotoAuditPage() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const [eventType, setEventType] = useState<PhotoAuditEventType | 'all'>('all');
  const [page, setPage] = useState(1);

  const params = useMemo(() => ({
    eventType: eventType === 'all' ? undefined : eventType,
    page, pageSize: 25,
  }), [eventType, page]);

  const { data, isLoading } = usePhotoAudit(params);
  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher /><ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-10 space-y-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t('common.back')}
        </Link>

        <div className="flex items-center gap-3">
          <Shield className="h-7 w-7 text-indigo-500" />
          <h1 className="font-display text-3xl text-foreground">{t('audit.photos.title')}</h1>
        </div>
        <p className="text-sm text-muted-foreground -mt-4">{t('audit.photos.description')}</p>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={eventType}
            onChange={(e) => { setEventType(e.target.value as PhotoAuditEventType | 'all'); setPage(1); }}
            className="h-9 px-3 rounded border border-border bg-background text-sm"
          >
            <option value="all">{t('audit.photos.filter.all')}</option>
            <option value="month_lock_overdue_override">{t('audit.photos.event.month_lock_overdue_override')}</option>
            <option value="late_photo_upload">{t('audit.photos.event.late_photo_upload')}</option>
            <option value="gallery_upload_override">{t('audit.photos.event.gallery_upload_override')}</option>
            <option value="confirm_without_photo">{t('audit.photos.event.confirm_without_photo')}</option>
          </select>
        </div>

        {/* List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/10 py-12 text-center">
            <Shield className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
            <h3 className="font-medium text-foreground mb-1">{t('audit.photos.empty.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('audit.photos.empty.description')}</p>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 border-b border-border">
                <tr>
                  <th className="text-start px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">{t('audit.photos.col.event')}</th>
                  <th className="text-start px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">{t('audit.photos.col.trip')}</th>
                  <th className="text-start px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">{t('audit.photos.col.user')}</th>
                  <th className="text-start px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">{t('audit.photos.col.reason')}</th>
                  <th className="text-end   px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">{t('audit.photos.col.when')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((ev) => <AuditRow key={ev.id} event={ev} />)}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              {t('common.page')} {page} / {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </main>

      <footer className="py-6 text-center text-xs text-muted-foreground border-t border-border">
        © Vida Group · Saudi Arabia
      </footer>
    </div>
  );
}

function AuditRow({ event }: { event: PhotoAuditEvent }) {
  const { t } = useTranslation();
  const Icon = EVENT_ICONS[event.eventType];
  const color = EVENT_COLORS[event.eventType];

  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-muted/20">
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5">
          <Icon className={`h-4 w-4 ${color}`} />
          <span className="font-medium">{t(`audit.photos.event.${event.eventType}`)}</span>
        </span>
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {event.trip ? event.trip.code : '—'}
      </td>
      <td className="px-3 py-2">
        <div className="font-medium">{event.user.fullNameEn}</div>
        <div className="text-xs text-muted-foreground">{event.user.role}</div>
      </td>
      <td className="px-3 py-2 max-w-md">
        <div className="text-sm truncate" title={event.reason || ''}>{event.reason || '—'}</div>
      </td>
      <td className="px-3 py-2 text-end text-muted-foreground text-xs whitespace-nowrap">
        {new Date(event.occurredAt).toLocaleString()}
      </td>
    </tr>
  );
}
